/**
 * src/features/auth/ui/PasswordSignInForm.tsx
 *
 * Email + password sign-in form (PWD-07). Three facts this file is built around:
 *   1. PWD-07 itself: a STATIC show/hide toggle node, an exact autocomplete pair
 *      (username / current-password), and a Caps Lock hint that lives in its own node —
 *      none of these are cosmetic, each protects a real password-manager or a11y behavior.
 *      The password field itself is the shared PasswordField.tsx (WR-03, 43-REVIEW.md) —
 *      this file no longer inlines its own copy of the toggle/Caps-Lock markup.
 *   2. WR-01: better-auth's client returns { data, error } and NEVER throws — the primary
 *      path is `if (result.error)`, not try/catch.
 *   3. AUTH-11: the server made "wrong password" and "unknown email" indistinguishable in
 *      Phase 40. This form shows exactly one shared credentials-error string for both cases —
 *      branching UI copy on the response body would silently reopen account enumeration
 *      that the server already closed.
 */

import { FC, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/shared/ui/button';
import { authClient } from '@/lib/auth-client';
import { setLastLoginMethod } from '../lib/lastLoginMethod';
import { MIN_PASSWORD_LENGTH } from '../lib/passwordPolicy';
import { PasswordField } from './PasswordField';

export interface PasswordSignInFormProps {
  /** Phase 43: switches LoginCard's email sub-method to the reset-request form. Optional
   * so existing render call sites (and any future consumer that has no reset flow) keep
   * compiling with zero changes. */
  onForgotPassword?: () => void;
  /** Phase 43: the address carried back via router navigation state after a successful
   * `/auth/reset` — read ONCE in a lazy initializer (same reasoning as LoginCard's
   * `lastMethod`: an effect would paint one frame with the wrong value first). The user can
   * still edit the field freely afterward; this only seeds the initial value. */
  initialEmail?: string;
}

export const PasswordSignInForm: FC<PasswordSignInFormProps> = ({ onForgotPassword, initialEmail }) => {
  const { t } = useTranslation();

  const [email, setEmail] = useState(() => initialEmail ?? '');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Synchronous in-flight guard: React state updates are batched, so two `fireEvent.submit`
  // calls issued back-to-back (before any re-render happens) would both see `isLoading ===
  // false` if that were the only guard. A ref mutates immediately, so the second call in the
  // same tick correctly observes the first call already in flight (PWD-07 / idempotency).
  const inFlightRef = useRef(false);

  // Focus email on mount — same convention as MagicLinkForm's emailRef.
  const emailRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (inFlightRef.current) return;
    const trimmedEmail = email.trim();
    if (!trimmedEmail || password.length < MIN_PASSWORD_LENGTH) return;

    inFlightRef.current = true;
    setIsLoading(true);
    setError(null);

    // WR-01/Pattern 1: better-auth returns { data, error }, NEVER throws. Primary path is
    // `if (result.error)` — no try/catch as the sole handler.
    const result = await authClient.signIn.email({ email: trimmedEmail, password });
    if (result.error) {
      if (result.error.status === 429) {
        // RATE-01: a separate axis from AUTH-11 — a rate limit doesn't disclose account
        // existence. No wait-time is interpolated: X-Retry-After never reaches this object
        // (42-RESEARCH.md Pitfall 1).
        setError(
          t('auth.password.rate_limited', 'Too many attempts. Please wait a minute and try again.'),
        );
      } else {
        // AUTH-11: single message for both wrong-password AND unknown-email — never
        // differentiate by response body/code.
        setError(t('auth.password.error', 'Incorrect email or password.'));
      }
    } else {
      // AUTH-09: write the remembered method ONLY on confirmed success — a wrong password
      // or a rate-limited attempt must never re-teach the screen (42-UI-SPEC.md §2).
      setLastLoginMethod('password');
    }
    // No manual session.refetch()/navigation on success — atomListeners already handles it
    // (42-RESEARCH.md Pattern 3); duplicating it would race the built-in mechanism.
    inFlightRef.current = false;
    setIsLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <label className="flex flex-col gap-1">
        <span className="text-body font-medium">{t('auth.email.label', 'Email')}</span>
        <input
          ref={emailRef}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('auth.email.placeholder', 'you@example.com')}
          required
          autoComplete="username"
          disabled={isLoading}
          className="h-11 border border-slate-200 dark:border-slate-700 rounded-input px-3 text-body focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-400 disabled:opacity-50"
        />
      </label>
      <PasswordField
        value={password}
        onChange={setPassword}
        label={t('auth.password.label', 'Password')}
        autoComplete="current-password"
        disabled={isLoading}
      />
      <Button
        variant="default"
        size="lg"
        type="submit"
        className="w-full"
        disabled={isLoading || !email.trim() || password.length < MIN_PASSWORD_LENGTH}
        aria-busy={isLoading}
      >
        {isLoading ? t('auth.submit_loading', 'Sending...') : t('auth.password.cta', 'Sign in')}
      </Button>
      {error && (
        <p className="text-body text-red-600 dark:text-red-400 text-center" role="alert">
          {error}
        </p>
      )}
      {onForgotPassword && (
        <Button variant="link" type="button" onClick={onForgotPassword} className="self-center">
          {t('auth.method.forgot_password', 'Forgot password?')}
        </Button>
      )}
    </form>
  );
};
